import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Gem, ShoppingCart, Sparkles, AlertCircle, Wallet, Gift, Clock, Calendar, Medal, ChevronUp, ChevronDown, Eye } from 'lucide-react';
import { diamondsService } from '@/services/diamonds.service';

interface PeriodoObj {
  inicio: string;
  fim: string;
}

interface DiamondStats {
  total_diamantes: number;
  total_vendas?: number;
  total_compras?: number;
  periodo: PeriodoObj;
}

interface Top5Item {
  user_id: number;
  nome: string;
  role: string;
  total_diamantes: number;
}

interface AccumulatedData {
  total_diamantes: number;
  total_usuarios: number;
  top5: Top5Item[];
  periodo: PeriodoObj;
}

interface RedeemedData {
  total_diamantes: number;
  total_resgates: number;
  periodo: PeriodoObj;
}

interface ExpiringData {
  total_usuarios: number;
  dias: number;
}

type PeriodOption = '7' | '30' | '90' | 'custom';

function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

function getDateRange(period: PeriodOption, customStart?: string, customEnd?: string): { startDate: string; endDate: string } {
  if (period === 'custom' && customStart && customEnd) {
    return { startDate: customStart, endDate: customEnd };
  }
  const days = parseInt(period);
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);
  return { startDate: formatDate(start), endDate: formatDate(end) };
}

export const DiamondDashboard: React.FC = () => {
  const [salesData, setSalesData] = useState<DiamondStats | null>(null);
  const [purchasesData, setPurchasesData] = useState<DiamondStats | null>(null);
  const [accumulatedData, setAccumulatedData] = useState<AccumulatedData | null>(null);
  const [redeemedData, setRedeemedData] = useState<RedeemedData | null>(null);
  const [expiringData, setExpiringData] = useState<ExpiringData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedPeriod, setSelectedPeriod] = useState<PeriodOption>('30');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const [diamondRanking, setDiamondRanking] = useState<any[]>([]);
  const [diamondSearchTerm, setDiamondSearchTerm] = useState('');
  const [diamondSelectedRoles, setDiamondSelectedRoles] = useState<string[]>(['revendedora', 'cliente', 'admin', 'coordenadora']);
  const [diamondFiltersExpanded, setDiamondFiltersExpanded] = useState(false);
  const [top5ModalOpen, setTop5ModalOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { startDate, endDate } = getDateRange(selectedPeriod, customStartDate, customEndDate);
      const days = selectedPeriod === 'custom' ? undefined : parseInt(selectedPeriod);

      const [salesDataResponse, purchasesDataResponse, accumulatedResponse, redeemedResponse, expiringResponse, rankingResponse] = await Promise.all([
        diamondsService.getDiamondsBySales(startDate, endDate),
        diamondsService.getDiamondsByPurchases(startDate, endDate),
        diamondsService.getDiamondsAccumulated(startDate, endDate),
        diamondsService.getDiamondsRedeemed(startDate, endDate),
        diamondsService.getDiamondsExpiring(days),
        diamondsService.getRanking()
      ]);

      setSalesData(salesDataResponse.data);
      setPurchasesData(purchasesDataResponse.data);
      setAccumulatedData(accumulatedResponse.data);
      setRedeemedData(redeemedResponse.data);
      setExpiringData(expiringResponse.data);
      setDiamondRanking(rankingResponse);
      setLoading(false);
    } catch (err) {
      console.error('Full error:', err);
      setError(err instanceof Error ? err.message : 'Erro desconhecido ao carregar dados');
      setLoading(false);
    }
  }, [selectedPeriod, customStartDate, customEndDate]);

  useEffect(() => {
    if (selectedPeriod === 'custom' && (!customStartDate || !customEndDate)) return;
    fetchData();
  }, [fetchData, selectedPeriod, customStartDate, customEndDate]);

  const toggleRoleFilter = (role: string) => {
    setDiamondSelectedRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  };

  const getFilteredDiamondRanking = () => {
    return diamondRanking.filter(user => {
      const matchesSearch = user.nome_razao_social.toLowerCase().includes(diamondSearchTerm.toLowerCase());
      const matchesRole = diamondSelectedRoles.includes(user.role);
      return matchesSearch && matchesRole;
    });
  };

  const periodLabel = selectedPeriod === 'custom'
    ? `${customStartDate} a ${customEndDate}`
    : `Últimos ${selectedPeriod} dias`;

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center min-h-40">
            <div className="text-center">
              <div className="animate-spin mb-4">
                <Gem className="w-8 h-8 text-primary mx-auto" />
              </div>
              <p className="text-muted-foreground">Carregando dados de diamantes...</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 text-destructive">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <div>
              <p className="font-semibold">Erro ao carregar dados</p>
              <p className="text-sm text-destructive/80">{error}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const totalDiamantes = (salesData?.total_diamantes || 0) + (purchasesData?.total_diamantes || 0);

  return (
    <div className="space-y-4">
      <div className='flex gap-4'>
        <div className='w-[70%]'>
          {/* Seletor de Período */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-muted-foreground">Período:</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: '7' as PeriodOption, label: '7 dias' },
                    { value: '30' as PeriodOption, label: '30 dias' },
                    { value: '90' as PeriodOption, label: '90 dias' },
                    { value: 'custom' as PeriodOption, label: 'Personalizado' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setSelectedPeriod(opt.value)}
                      className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                        selectedPeriod === opt.value
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background text-foreground border-border hover:bg-muted'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {selectedPeriod === 'custom' && (
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={customStartDate}
                      max={customEndDate || formatDate(new Date())}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="px-2 py-1.5 text-sm rounded-md border border-border bg-background"
                    />
                    <span className="text-sm text-muted-foreground">até</span>
                    <input
                      type="date"
                      value={customEndDate}
                      max={formatDate(new Date())}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="px-2 py-1.5 text-sm rounded-md border border-border bg-background"
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
            {/* Card 1: Total Geral */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-primary" />
                  </div>
                  <span>Total Geral</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Total de Diamantes Gerados</p>
                  <p className="text-3xl font-bold text-foreground">
                    {totalDiamantes.toLocaleString('pt-BR')}
                  </p>
                </div>
                <div className="pt-3 border-t border-border">
                  <p className="text-xs text-muted-foreground">Período</p>
                  <p className="text-sm font-semibold text-foreground">{periodLabel}</p>
                </div>
              </CardContent>
            </Card>

            {/* Card 2: Diamantes por Vendas */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                    <Gem className="w-4 h-4 text-primary" />
                  </div>
                  <span>Diamantes por Venda</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Total de Diamantes</p>
                  <p className="text-3xl font-bold text-foreground">
                    {salesData ? salesData.total_diamantes.toLocaleString('pt-BR') : '0'}
                  </p>
                </div>
                <div className="pt-3 border-t border-border">
                  <p className="text-xs text-muted-foreground">Transações de Venda</p>
                  <p className="text-lg font-semibold text-foreground">
                    {salesData?.total_vendas || 0}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Card 3: Diamantes por Compras de Clientes */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                    <ShoppingCart className="w-4 h-4 text-primary" />
                  </div>
                  <span>Diamantes por Compra</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Total de Diamantes</p>
                  <p className="text-3xl font-bold text-foreground">
                    {purchasesData ? purchasesData.total_diamantes.toLocaleString('pt-BR') : '0'}
                  </p>
                </div>
                <div className="pt-3 border-t border-border">
                  <p className="text-xs text-muted-foreground">Compras de Clientes</p>
                  <p className="text-lg font-semibold text-foreground">
                    {purchasesData?.total_compras || 0}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Row 2: Acumulados, Resgatados, Expirando */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
            {/* Card 4: Diamantes Acumulados */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between text-base">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                      <Wallet className="w-4 h-4 text-primary" />
                    </div>
                    <span>Acumulados por Rede</span>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Total de Diamantes</p>
                  <p className="text-3xl font-bold text-foreground">
                    {accumulatedData ? accumulatedData.total_diamantes.toLocaleString('pt-BR') : '0'}
                  </p>
                </div>
                <div className="pt-3 border-t border-border">
                  <p className="text-xs text-muted-foreground">Usuários Acumuladores</p>
                  <div className='flex items-center gap-2'>
                    <p className="text-lg font-semibold text-foreground">
                      {accumulatedData?.total_usuarios || 0}
                    </p>
                    {accumulatedData?.top5 && accumulatedData.top5.length > 0 && (
                      <button
                        onClick={() => setTop5ModalOpen(true)}
                        className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-muted hover:bg-muted/80 transition-colors"
                        title="Ver Top 5 na Rede"
                      >
                        <Eye className="w-3.5 h-3.5 text-primary" />
                        <span className="text-xs font-medium text-muted-foreground">Ver Top 5</span>
                      </button>
                    )}
                  </div>
                  
                </div>
              </CardContent>
            </Card>

            {/* Modal Top 5 Acumulados na Rede */}
            <Dialog open={top5ModalOpen} onOpenChange={setTop5ModalOpen}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Wallet className="w-5 h-5 text-primary" />
                    Top 5 Acumuladores por Rede
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-3 pt-2">
                  {accumulatedData?.top5 && accumulatedData.top5.length > 0 ? (
                    accumulatedData.top5.map((item, index) => (
                      <div key={item.user_id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white text-xs ${
                            index === 0 ? 'bg-yellow-500' :
                            index === 1 ? 'bg-gray-400' :
                            index === 2 ? 'bg-orange-600' :
                            'bg-emerald-500'
                          }`}>
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-semibold text-sm">{item.nome}</p>
                            <p className="text-xs text-muted-foreground">{item.role === 'revendedora' ? 'Revendedora' : 'Cliente'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Gem className="w-4 h-4 text-primary" />
                          <span className="font-bold text-sm text-foreground">{item.total_diamantes.toLocaleString('pt-BR')}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted-foreground text-center py-4 text-sm">Nenhum dado disponível</p>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            {/* Card 5: Diamantes Resgatados */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                    <Gift className="w-4 h-4 text-primary" />
                  </div>
                  <span>Diamantes Resgatados</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Total Resgatado</p>
                  <p className="text-3xl font-bold text-foreground">
                    {redeemedData ? redeemedData.total_diamantes.toLocaleString('pt-BR') : '0'}
                  </p>
                </div>
                <div className="pt-3 border-t border-border">
                  <p className="text-xs text-muted-foreground">Resgates aprovados</p>
                  <p className="text-lg font-semibold text-foreground">
                    {redeemedData?.total_resgates || 0}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Card 6: Diamantes Expirando */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                    <Clock className="w-4 h-4 text-primary" />
                  </div>
                  <span>Expirando em {expiringData?.dias || 30} dias</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Usuários afetados</p>
                  <p className="text-3xl font-bold text-foreground">
                    {expiringData?.total_usuarios || 0}
                  </p>
                </div>
                {(!expiringData || expiringData.total_usuarios === 0) && (
                  <div className="pt-3 border-t border-border">
                    <p className="text-sm text-muted-foreground">Nenhum diamante expirando</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
        <div className='w-[30%]'>
          {/* Ranking de Diamantes */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Medal className="w-5 h-5 text-primary" />
                Top Diamantes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <button
                  onClick={() => setDiamondFiltersExpanded(!diamondFiltersExpanded)}
                  className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors"
                >
                  <span className="text-sm font-medium">Filtros</span>
                  {diamondFiltersExpanded ? (
                    <ChevronUp className="w-4 h-4 text-gray-600" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-600" />
                  )}
                </button>

                {diamondFiltersExpanded && (
                  <div className="space-y-4 pt-2 border-t">
                    <div>
                      <label className="text-sm font-medium mb-2 block">Buscar por Nome</label>
                      <input
                        type="text"
                        placeholder="Digite um nome..."
                        value={diamondSearchTerm}
                        onChange={(e) => setDiamondSearchTerm(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Filtrar por Tipo</label>
                      <div className="flex flex-wrap gap-3">
                        {[
                          { value: 'revendedora', label: 'Revendedora' },
                          { value: 'cliente', label: 'Cliente' },
                          { value: 'admin', label: 'Admin' },
                          { value: 'coordenadora', label: 'Coordenadora' }
                        ].map(role => (
                          <div key={role.value} className="flex items-center">
                            <input
                              type="checkbox"
                              id={`diamond-role-${role.value}`}
                              checked={diamondSelectedRoles.includes(role.value)}
                              onChange={() => toggleRoleFilter(role.value)}
                              className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                            />
                            <label
                              htmlFor={`diamond-role-${role.value}`}
                              className="ml-2 text-sm text-gray-700 cursor-pointer whitespace-nowrap"
                            >
                              {role.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                <div className="max-h-[360px] overflow-y-auto border-t pt-4">
                  <div className="space-y-3">
                    {getFilteredDiamondRanking().length > 0 ? (
                      getFilteredDiamondRanking().map((user, index) => (
                        <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3 flex-1">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white text-xs ${
                              index === 0 ? 'bg-yellow-500' :
                              index === 1 ? 'bg-gray-400' :
                              index === 2 ? 'bg-orange-600' :
                              'bg-blue-500'
                            }`}>
                              {index + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm max-w-[140px] truncate">{user.nome_razao_social}</p>
                              <p className="text-xs text-muted-foreground">#{user.codigo} - {user.role === 'coordenadora' ? 'Coordenadora' : user.role.charAt(0).toUpperCase() + user.role.slice(1)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-2">
                            <Gem className="w-4 h-4 text-primary" />
                            <p className="font-bold text-sm">{user.diamantes}</p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-muted-foreground text-center py-8 text-sm">Nenhum resultado encontrado</p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    
    
    </div>
  );
};

export default DiamondDashboard;
